import { useRouter } from 'next/router';

import { DynamicObject } from 'src/utils/commonTypes';

import { useAppStore } from 'src/hooks/useAppStore';
import { BEST_VIEWED_ON } from '../../utils/constants/FooterConstants';
import {
  CountryCode,
  FOOTER_URLS_CONSTANTS,
  PageTypes
} from '../../utils/constants/stringConstants';
import {
  DEVICE_WIDTH_CONSTANTS,
  FeatureFlagConstants
} from '../../utils/constants/utilConstants';
import FooterMenu from '../footerMenu/FooterMenu';
import * as style from './FooterStyle';
import MegaMenuFooter from './megaMenuFooter/MegaMenuFooter';
import {
  childFooterMovieGlobal,
  childFooterMoviesData,
  childFooterSeriesData,
  childFooterSeriesGlobal,
  childFooterShowsData,
  childFooterShowsGlobal,
  megaMenuFooterDataRegional,
  MegaMenuFooterDataGlobal
} from './MegaMenuFooterData';
import SocialMediaIconsContainer from './socialMediaIconsContainer/SocialMediaIconsContainer';

import { footerApiGenerator } from './FooterApiHelper';
import { useEffect, useState } from 'react';
import SimpleStore from 'src/store/SimpleStore';
import { HTTP_URL_PARAMS } from 'src/utils/constants/HttpMethodsConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';

//Data types
export type itemsDataType = {
  title: string;
  slug: string;
  external?: boolean;
};
export type footerDataType = {
  title: string;
  slug?: string;
  items: itemsDataType[];
};
type SubscriptionFooterType = {
  footerStrings: DynamicObject;
  year: string;
};
const childFooterDataObject = {
  [FOOTER_URLS_CONSTANTS.TV_SHOWS]: (global: boolean) =>
    global ? childFooterShowsGlobal : childFooterShowsData,
  [FOOTER_URLS_CONSTANTS.EPISODES]: (global: boolean) =>
    global ? childFooterShowsGlobal : childFooterShowsData,
  [FOOTER_URLS_CONSTANTS.MOVIES]: (global: boolean) =>
    global ? childFooterMovieGlobal : childFooterMoviesData,
  [FOOTER_URLS_CONSTANTS.SERIES]: (global: boolean) =>
    global ? childFooterSeriesGlobal : childFooterSeriesData,
  [FOOTER_URLS_CONSTANTS.ORIGINALS]: (global: boolean) =>
    global ? childFooterSeriesGlobal : childFooterSeriesData
  /** For live tv child footer data, content needs to be filtered based on country logic.
   * Please consult PWA code for the same **/
};

//commented following function since it not used anymore, can be uncommented if needed
/**
 * @returns {{show,data}} the correct child footer data based on url
 */
const getChildFooterData = (isGlobal: boolean, pathname: string) => {
  if (pathname?.includes(FOOTER_URLS_CONSTANTS.KIDS)) return null;

  const childFooterDataKey = Object.keys(childFooterDataObject).find((slug) =>
    pathname?.includes(slug)
  );

  if (childFooterDataKey) {
    return childFooterDataObject[childFooterDataKey](isGlobal);
  }
  return null;
};

const SubscriptionFooter = ({
  footerStrings,
  year
}: SubscriptionFooterType) => {
  return (
    <footer
      data-testid="subscription-footer"
      css={style.footerContainerSubscription}>
      <div css={style.copyrightText}>
        {footerStrings.COPYRIGHT.replace('{YEAR}', year)}
      </div>
      <FooterMenu footerStrings={footerStrings} />
    </footer>
  );
};

const useFooterData = (childFooterDataKey: string | undefined) => {
  const [megaMenuFooterData, setMegaMenuFooterData] = useState<
    footerDataType[] | null
  >(null);
  const [childMenuFooterData, setChildMenuFooterData] = useState<
    footerDataType[] | null
  >(null);
  const { usersLocationData, usersPreferenceData, remoteConfig } =
    useAppStore();
  const featureFlag = isFeatureEnabled(
    remoteConfig,
    FeatureFlagConstants.FEATURE_FOOTER_THROUGH_API
  );

  useEffect(() => {
    if (featureFlag) {
      const fetchMegaMenuFooterData = async () => {
        const data = await footerApiGenerator(
          usersLocationData?.country_code ||
            SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE),
          usersPreferenceData?.display_language ||
            SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE)
        ); //global is handled within footerApiGenerator() with ccode
        setMegaMenuFooterData(data);
      };
      fetchMegaMenuFooterData();
    }
  }, []);

  useEffect(() => {
    if (featureFlag && childFooterDataKey) {
      const fetchChildMenuFooterData = async () => {
        const data = await footerApiGenerator(
          usersLocationData?.country_code ||
            SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE),
          usersPreferenceData?.display_language ||
            SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE),
          true,
          childFooterDataKey
        );
        setChildMenuFooterData(data);
      };
      fetchChildMenuFooterData();
    }
  }, [childFooterDataKey]);

  return { megaMenuFooterData, childMenuFooterData };
};

const Footer = () => {
  const { translations, usersLocationData, appConfig, initialLaunchData } =
    useAppStore();
  const router = useRouter();
  //TODO: Add pageName array logic for different kind of pages eg. subscription, payment etc.
  const isSubscription =
    appConfig.pageType === PageTypes.SUBSCRIPTION_PAGE ||
    appConfig.pageType === PageTypes.REDIRECTION_PAGE;
  const footerStrings = translations?.common;
  let isGlobal = false;
  if (usersLocationData) {
    isGlobal = usersLocationData.country_code !== CountryCode.IN;
  }
  const pathname = router?.pathname || '';
  const showChildFooter = pathname.includes(FOOTER_URLS_CONSTANTS.KIDS)
    ? false
    : Object.keys(childFooterDataObject).some((slug) =>
        pathname.includes(slug)
      );
  const childFooterDataKey = showChildFooter
    ? Object.keys(childFooterDataObject).find((slug) => pathname.includes(slug))
    : undefined;

  let { megaMenuFooterData, childMenuFooterData } =
    useFooterData(childFooterDataKey);

  if (!megaMenuFooterData && !showChildFooter) {
    //if didnt obtain mega menu api data, then assign local data
    megaMenuFooterData = isGlobal
      ? MegaMenuFooterDataGlobal
      : megaMenuFooterDataRegional;
  }

  if (!childMenuFooterData && showChildFooter) {
    //if didnt obtain child menu api data, then assign local data
    childMenuFooterData = getChildFooterData(isGlobal, router?.pathname);
  }

  const year = new Date().getFullYear().toString();

  if (
    isSubscription &&
    (appConfig.deviceWidth as number) <= DEVICE_WIDTH_CONSTANTS.deviceWidth600
  ) {
    return null;
  }

  return footerStrings ? (
    isSubscription ? (
      <SubscriptionFooter footerStrings={footerStrings} year={year} />
    ) : (
      <>
        <div
          data-testid="footer-main-div"
          css={[
            showChildFooter &&
            childMenuFooterData &&
            childMenuFooterData.length > 0
              ? style.footerContainer
              : null
          ]}>
          {showChildFooter &&
            childMenuFooterData &&
            childMenuFooterData.length > 0 && (
              <MegaMenuFooter data={childMenuFooterData} />
            )}
        </div>
        <footer css={[style.footerContainer]}>
          <SocialMediaIconsContainer
            countryList={initialLaunchData?.countrylist}
            global={isGlobal}
          />
          <div css={[style.footerMenuContainer]}>
            <FooterMenu footerStrings={footerStrings} />
          </div>
          {megaMenuFooterData && <MegaMenuFooter data={megaMenuFooterData} />}
          <div css={style.copyrightText} data-testid="copyright-block">
            <div>{BEST_VIEWED_ON}</div>
            {footerStrings.COPYRIGHT.replace('{YEAR}', year)}
          </div>
        </footer>
      </>
    )
  ) : null;
};

export default Footer;
